// 「最近活動」每一筆寫成一句白話（管理頁的「最近活動」、導演頁的「活動」、動畫師工作站的「活動」共用）
//
// 以前沒列到的種類會直接把後台的代號印出來（「建立 song_video」「新增mechanism」），
// 交新版後自動回到審查中那一筆沒有歌名，會寫成「把歌曲「undefined」…」。
//
// 修改類的活動要寫出「改了什麼」：
//   新的紀錄：後台（cf-worker/activityDiff.mjs）把每個改到的欄位的前後值放在 payload.diff
//            = [{ field, from?, to? }]，值都是給人看的（名字、數字），沒有 id；沒有 from / to = 只知道有改
//   舊的紀錄：只有 payload.changes（只有新值，欄位名是程式裡的名字）或 payload.fields（版型）→ 盡量照新格式寫；
//            什麼都沒有的維持以前的句子
//   不認識的欄位一律寫「其他設定」，不會印出英文欄位名。一行寫不下就只寫前 3 項加「等 N 項」。
import type { ActivityEntry } from './api';
import { MEMBER_ROLE_NAME, type MemberRole } from './projectMembers';
import { parseWorkScale, workScaleText } from './workScale';

const SONG_STATUS_LABEL: Record<string, string> = {
  todo: '待製作', in_review: '審查中', approved: '已通過', needs_changes: '需修改',
};

/**
 * 活動的對象（target_type）→ 白話名稱。後台每寫一種新的 target_type，這裡就要有；
 * activityText.test.ts 會掃 cf-worker 全部寫活動紀錄的地方比對，漏了測試會失敗（不然畫面會退回「項目」）。
 */
export const TARGET_LABEL: Record<string, string> = {
  project: '專案', song: '歌曲', cue: 'cue', library_cue: 'Cue 庫的 cue', cue_state: '物件位置', song_cue_states: '物件位置', stage_object: '物件', model: '模型',
  song_video: '影片', song_music: '音樂', mechanism: '機關', mechanism_group: '一排機關', drive_file: 'Drive 檔案', cue_template: '常用擺位', invite: '邀請連結',
  share_link: '分享連結', comment: '留言', member: '成員',
  led_template: '版型', led_screen: 'LED 螢幕', song_template_timeline: '版型時間線',
  ae_package: 'AE 打包檔', song_gate: '審查',
};

/** 活動的動作（action）→ 白話動詞。同樣，後台新增動作這裡要有。 */
export const ACTION_VERB: Record<string, string> = {
  create: '新增', update: '修改', delete: '刪除', reorder: '重新排序',
  reset: '重置', activate: '切換', archive: '封存', upload: '上傳', bulk_create: '批次建立', seed: '加入範例', restore: '救回', purge: '清掉',
};

// ─────────────────────────────────────────────
// 修改了哪些欄位：欄位的白話名稱、值怎麼寫
// ─────────────────────────────────────────────

type FieldKind = 'text' | 'num' | 'bool' | 'enum' | 'clock' | 'seconds' | 'none';
interface FieldSpec {
  label: string;
  kind?: FieldKind;
  /** 值是 null / 空的時候怎麼寫（預設「空白」） */
  empty?: string;
  /** enum：值 → 白話 */
  values?: Record<string, string>;
  /** bool：變成 true / false 時怎麼寫 */
  on?: string;
  off?: string;
  /** num：單位（接在數字後面） */
  unit?: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  led_panel: 'LED 面板', mechanism: '機關', walk_point: '走位點', fixture: '燈光/道具', performer: '表演者', other: '其他',
};
const PROJECT_STATUS_LABEL: Record<string, string> = { active: '進行中', in_review: '審查中', archived: '封存' };

const FIELDS: Record<string, Record<string, FieldSpec>> = {
  song: {
    name: { label: '歌名' },
    code: { label: '代號', empty: '沒有' },
    animator: { label: '負責動畫師', empty: '還沒指派' },
    template: { label: '版型', empty: '全部' },
    order: { label: '順序', kind: 'none' },
    drive: { label: 'Drive 資料夾', kind: 'none' },
    status: { label: '狀態', kind: 'enum', values: SONG_STATUS_LABEL },
    start: { label: '起始位置' },
    length: { label: '歌曲長度', empty: '還沒設', kind: 'seconds' },
  },
  project: {
    name: { label: '專案名稱' },
    status: { label: '專案狀態', kind: 'enum', values: PROJECT_STATUS_LABEL },
    description: { label: '說明', kind: 'none' },
    tags: { label: '標籤', kind: 'none' },
    show: { label: '所屬 Show', kind: 'none' },
    drive: { label: 'Drive 來源', kind: 'none' },
    scale_correction: { label: '校準倍率', kind: 'num' },
    led_canvas: { label: 'LED 畫布大小', empty: '還沒填' },
    watermark_members: { label: '成員看片浮水印', kind: 'bool', on: '開啟成員看片浮水印', off: '關閉成員看片浮水印' },
    look_settings: { label: '現場設定', kind: 'none' },
    material_overrides: { label: '材質', kind: 'none' },
    home_view: { label: '預設視角', kind: 'none' },
  },
  cue: {
    name: { label: '名稱' },
    order: { label: '順序', kind: 'none' },
    position: { label: '位置', kind: 'none' },
    rotation: { label: '角度', kind: 'none' },
    fov: { label: '視角', kind: 'num', unit: '°' },
    crossfade: { label: '轉場', kind: 'seconds' },
    video_time: { label: '影片時間', kind: 'clock', empty: '沒設' },
    status: { label: '狀態', kind: 'enum', values: { master: '正式', proposal: '提案', alternate: '備案' } },
  },
  stage_object: {
    name: { label: '名稱' },
    category: { label: '類型', kind: 'enum', values: CATEGORY_LABEL },
    locked: { label: '鎖定', kind: 'bool', on: '鎖定', off: '解除鎖定' },
    default_hidden: { label: '預設隱藏', kind: 'bool', on: '設成預設隱藏', off: '取消預設隱藏' },
    material: { label: '材質', kind: 'none' },
    led: { label: 'LED 設定', kind: 'none' },
  },
  mechanism: {
    name: { label: '名稱' },
    kind: { label: '種類', kind: 'enum', values: { linear: '直線移動', rotary: '旋轉' } },
    axis: { label: '方向', kind: 'none' },
    pivot: { label: '轉軸位置', kind: 'none' },
    travel_min: { label: '行程下限', kind: 'num' },
    travel_max: { label: '行程上限', kind: 'num' },
    default_value: { label: '預設位置', kind: 'num' },
    max_speed: { label: '最高速度', kind: 'num', empty: '不限' },
    presets: { label: '常用位置', kind: 'none' },
    parent: { label: '上一層機關', kind: 'none' },
    objects: { label: '帶的物件', kind: 'none' },
    console_label: { label: '控台編號', empty: '沒有' },
    variant: { label: '機關形式', kind: 'none' },
    mirror: { label: '對開的另一邊', kind: 'none' },
    arc_radius: { label: '軌道半徑', kind: 'num' },
    metadata: { label: '備註', kind: 'none' },
    hoists: { label: '吊點', kind: 'none' },
  },
  mechanism_group: {
    name: { label: '名稱' },
    travel_min: { label: '行程下限', kind: 'num' },
    travel_max: { label: '行程上限', kind: 'num' },
    default_value: { label: '預設位置', kind: 'num' },
    max_speed: { label: '最高速度', kind: 'num', empty: '不限' },
    presets: { label: '常用位置', kind: 'none' },
  },
  led_template: {
    name: { label: '名稱' },
    canvas: { label: '畫布大小' },
    scale_pct: { label: '縮放', kind: 'num', unit: '%' },
    cabinet: { label: '箱體大小', empty: '沒設' },
    screens: { label: '螢幕位置', kind: 'none' },
  },
  led_screen: {
    name: { label: '名稱' },
    uv: { label: '切的範圍', kind: 'none' },
  },
};

const OTHER_SPEC: FieldSpec = { label: '其他設定', kind: 'none' };

/** 舊紀錄的 changes 欄位名稱 → [新的欄位, 值怎麼拿]；'to' = 值就是改成的內容，沒寫 = 只知道有改 */
type LegacyHow = 'to' | ((v: unknown) => unknown);
const NO_VALUE = Symbol('no-value');
/** id 看不懂：空的 = 清掉（null），有值 = 只知道有改 */
const clearedOnly: LegacyHow = (v) => (v ? NO_VALUE : null);
const asBool: LegacyHow = (v) => (v === true || v === 1 || v === '1');
/** 舊的 led_canvas 是 JSON 字串 */
const canvasOf: LegacyHow = (v) => {
  let o: unknown = v;
  if (typeof v === 'string') { try { o = JSON.parse(v); } catch { return NO_VALUE; } }
  if (o === null) return null;
  const w = Number((o as Record<string, unknown>)?.width), h = Number((o as Record<string, unknown>)?.height);
  return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? `${w}×${h}` : NO_VALUE;
};
const videoTimeOf: LegacyHow = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

const LEGACY: Record<string, Record<string, [string, LegacyHow?]>> = {
  song: {
    name: ['name', 'to'], code: ['code', 'to'], animator_user_id: ['animator', clearedOnly], template_id: ['template', clearedOnly],
    order: ['order'], drive_folder_id: ['drive'], status: ['status', 'to'],
  },
  project: {
    name: ['name', 'to'], status: ['status', 'to'], description: ['description'], tags: ['tags'],
    show_id: ['show'], showId: ['show'],
    drive_folder_id: ['drive'], drive_filename_pattern: ['drive'], drive_oauth_token_id: ['drive'],
    scale_correction: ['scale_correction', 'to'], scaleCorrection: ['scale_correction', 'to'],
    look_settings: ['look_settings'], lookSettings: ['look_settings'],
    material_overrides: ['material_overrides'], materialOverrides: ['material_overrides'],
    home_view: ['home_view'], homeView: ['home_view'],
    led_canvas: ['led_canvas', canvasOf], ledCanvas: ['led_canvas', canvasOf],
    watermark_members: ['watermark_members', asBool], watermarkMembers: ['watermark_members', asBool],
  },
  cue: {
    name: ['name', 'to'], order: ['order'], position: ['position'], rotation: ['rotation'],
    fov: ['fov', 'to'], crossfadeSeconds: ['crossfade', 'to'], videoTimeSec: ['video_time', videoTimeOf], status: ['status', 'to'],
  },
  stage_object: {
    displayName: ['name', 'to'], category: ['category', 'to'], locked: ['locked', asBool], defaultHidden: ['default_hidden', asBool],
    materialProps: ['material'], ledProps: ['led'],
  },
  mechanism: {
    name: ['name', 'to'], kind: ['kind', 'to'], axis: ['axis'], pivot: ['pivot'], travelMin: ['travel_min', 'to'], travelMax: ['travel_max', 'to'],
    defaultValue: ['default_value', 'to'], maxSpeed: ['max_speed', 'to'], presets: ['presets'], parentId: ['parent'], objectIds: ['objects'],
    objectSides: ['objects'], consoleLabel: ['console_label', 'to'], variant: ['variant'], mirror: ['mirror'], arcRadius: ['arc_radius', 'to'],
    metadata: ['metadata'], hoists: ['hoists'],
  },
  mechanism_group: {
    name: ['name', 'to'], travelMin: ['travel_min', 'to'], travelMax: ['travel_max', 'to'], defaultValue: ['default_value', 'to'],
    maxSpeed: ['max_speed', 'to'], presets: ['presets'],
  },
  led_template: {
    name: ['name'], canvas: ['canvas'], scalePct: ['scale_pct'], cabinet: ['cabinet'], screens: ['screens'],
  },
};
/** 舊紀錄裡這些是操作用的，不是「改了什麼」 */
const LEGACY_SKIP = new Set(['reason', 'moveObjects']);
/** 機關、專案的舊紀錄是把整張表單送出去的欄位，項數太多看不出改了什麼 → 維持以前的句子 */
const LEGACY_WHOLE_FORM_MIN = 8;

interface Change { field: string; hasFrom: boolean; from?: unknown; hasTo: boolean; to?: unknown }

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isPlain = (v: unknown) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/** 這一筆改了哪些欄位；什麼都不知道 = null（維持以前的句子），知道但是空的 = [] */
function changesOf(type: string, p: Record<string, unknown>): Change[] | null {
  if (Array.isArray(p.diff)) {
    return p.diff.filter(isObj).map(e => ({
      field: typeof e.field === 'string' ? e.field : '',
      hasFrom: 'from' in e, from: e.from, hasTo: 'to' in e, to: e.to,
    }));
  }
  const legacy = LEGACY[type];
  if (!legacy) return null;
  // 版型舊紀錄：fields = 這次送來的欄位名稱
  const keys: string[] | null = isObj(p.changes) ? Object.keys(p.changes)
    : (type === 'led_template' && Array.isArray(p.fields) ? p.fields.filter((k): k is string => typeof k === 'string') : null);
  if (!keys) return null;
  const src = isObj(p.changes) ? p.changes : {};
  const used = keys.filter(k => !LEGACY_SKIP.has(k));
  if ((type === 'mechanism' || type === 'project') && used.length >= LEGACY_WHOLE_FORM_MIN) return null;
  const out: Change[] = [];
  for (const k of used) {
    const m = legacy[k];
    if (!m) { out.push({ field: '', hasFrom: false, hasTo: false }); continue; }
    const [field, how] = m;
    const c: Change = { field, hasFrom: false, hasTo: false };
    if (how) {
      const v = how === 'to' ? src[k] : how(src[k]);
      if (v !== NO_VALUE && isPlain(v)) { c.hasTo = true; c.to = v; }
    }
    out.push(c);
  }
  return out;
}

/** 數字、秒數、時間碼 → 文字 */
const fmtNum = (n: number) => String(Math.round(n * 100) / 100);

function showValue(spec: FieldSpec, v: unknown): string {
  const kind = spec.kind ?? 'text';
  if (v === null || v === undefined || v === '') return spec.empty ?? '空白';
  if (kind === 'enum') return spec.values?.[String(v)] ?? '其他';
  if (kind === 'clock') return typeof v === 'number' ? fmtClock(v) : (spec.empty ?? '空白');
  if (kind === 'seconds') return typeof v === 'number' ? `${fmtNum(v)} 秒` : '空白';
  if (kind === 'num') return typeof v === 'number' ? `${fmtNum(v)}${spec.unit ?? ''}` : '空白';
  return String(v);
}

/** 一個欄位 → 一小段：「歌名 A → B」「轉場改成 2 秒」「改了位置」「鎖定」 */
function fragmentOf(type: string, c: Change): string {
  const spec = FIELDS[type]?.[c.field] ?? OTHER_SPEC;
  const kind = spec.kind ?? 'text';
  const changed = join('改了', spec.label);
  if (kind === 'none') return changed;
  if (kind === 'bool') {
    return c.hasTo && typeof c.to === 'boolean' ? ((c.to ? spec.on : spec.off) ?? changed) : changed;
  }
  if (c.hasTo && c.hasFrom) return `${spec.label} ${showValue(spec, c.from)} → ${showValue(spec, c.to)}`;
  if (c.hasTo) return `${spec.label}改成 ${showValue(spec, c.to)}`;
  return changed;
}

/** 這一筆的修改小段（一個欄位一段，重複的合在一起）；skip = 先不寫的欄位 */
function changeFragments(type: string, p: Record<string, unknown>, skip: string[] = []): string[] {
  const list = changesOf(type, p);
  if (!list) return [];
  const out: string[] = [];
  for (const c of list) {
    if (skip.includes(c.field)) continue;
    const f = fragmentOf(type, c);
    if (!out.includes(f)) out.push(f);
  }
  return out;
}

/** 句子後面接修改的項目：只寫前 3 項，多的寫「等 N 項」 */
function withDetails(head: string, frags: string[]): string {
  if (!frags.length) return head;
  return `${head}：${frags.slice(0, 3).join('、')}${frags.length > 3 ? ` 等 ${frags.length} 項` : ''}`;
}

// ─────────────────────────────────────────────

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
/** 中文接英文中間空一格（「新增 cue」「修改 Drive 檔案」） */
const join = (a: string, b: string) => (/^[A-Za-z]/.test(b) ? `${a} ${b}` : `${a}${b}`);
/** 83 → 1:23 */
const fmtClock = (sec: number) => { const t = Math.max(0, Math.floor(sec)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };
/** 「名稱」：有名字才加引號，沒有就不寫（不會出現 undefined） */
const quoted = (name: string | undefined) => (name ? `「${name}」` : '');
/** （a，b）：沒有東西就不寫 */
const paren = (bits: Array<string | undefined | false>) => {
  const list = bits.filter((b): b is string => !!b);
  return list.length ? `（${list.join('，')}）` : '';
};

/** 秒數：2 → 2、0.5 → 0.5 */
const fmtSec = (n: number) => String(Math.round(n * 100) / 100);
/** Cue 3「名稱」（編號是用戶自己打的文字） */
const cueName = (label: string | undefined, name: string | undefined) => `Cue${label ? ` ${label}` : ''}${quoted(name)}`;

/** 檔案大小：12582912 → 12 MB */
function fmtSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${Number((bytes / 1024 ** 3).toFixed(1))} GB`;
  if (bytes >= 1024 ** 2) return `${Number((bytes / 1024 ** 2).toFixed(1))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** 模型版本的檔名開頭是上傳時間（毫秒）→「11/2 14:30」；認不出來 = 空字串 */
function modelKeyTime(key: string | undefined): string {
  const m = key ? /(?:^|\/)(\d{12,14})(?=[-.])/.exec(key) : null;
  if (!m) return '';
  const d = new Date(Number(m[1]));
  const y = d.getFullYear();
  if (!(y >= 2020 && y <= 2100)) return '';
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function activityTargetLabel(t: string): string {
  return TARGET_LABEL[t] ?? '項目';
}
export function activityVerb(a: string): string {
  return ACTION_VERB[a] ?? '更新';
}

/** 一筆活動 → 一句話（不含是誰做的） */
export function describeActivity(e: Pick<ActivityEntry, 'action' | 'targetType' | 'payload'>): string {
  const p = (e.payload || {}) as Record<string, unknown>;
  const action = String(e.action);
  const type = String(e.targetType);
  const target = activityTargetLabel(type);
  const verb = activityVerb(action);
  const name = str(p.name);

  // Cue 庫（編號是用戶自己打的文字：「12」「3-1」）
  if (type === 'library_cue') {
    const lab = str(p.label) ?? '';
    const full = (l: string | undefined, n: string | undefined) => quoted([l, n].filter(Boolean).join(' '));
    if (action === 'create') {
      const bl = str(p.basedOnLabel), bn = str(p.basedOnName);
      // 導演在調整舞台複製 / 另存（另存會把這首歌的這個 cue 換成新的）
      if (p.kind === 'stage_copy' && (bl || bn)) return `從${full(bl, bn)}複製成${full(lab, name)}`;
      if (p.kind === 'stage_save_as' && (bl || bn)) {
        const song = str(p.songName);
        return `在調整舞台從${full(bl, bn)}另存成${full(lab, name)}${song ? `，歌曲${quoted(song)}改用它` : ''}`;
      }
      return bl || bn ? `從${full(bl, bn)}另存成${full(lab, name)}` : `新增 Cue 庫的 cue${full(lab, name)}`;
    }
    if (action === 'delete') return `刪除 Cue 庫的${full(lab, name)}`;
    if (action === 'update') {
      if (p.kind === 'rename') return `把${quoted(lab)}改名成${quoted(str(p.toName))}`;
      if (p.kind === 'relabel') return `把${full(str(p.fromLabel), name)}的編號改成 ${str(p.toLabel) ?? ''}`.trim();
      if (p.kind === 'crossfade') {
        const to = num(p.toSeconds);
        return `把 ${cueName(lab, name)}的轉場改成 ${to != null ? fmtSec(to) : '新的'} 秒（用到的歌一起變）`;
      }
      // 導演確認陣形：標已確認 / 退回待確認 / 確認後內容被改過、自動退回
      if (p.kind === 'confirm') return `確認${full(lab, name)}（已確認）`;
      if (p.kind === 'unconfirm') return `把${full(lab, name)}退回待確認`;
      if (p.kind === 'confirm_returned') {
        const songs = num(p.songCount);
        return `${full(lab, name)}內容被改過，已退回待確認${songs ? `（${songs} 首歌在用）` : ''}`;
      }
      if (p.kind === 'look') {
        const songs = num(p.songCount);
        return `更新 Cue 庫的${full(lab, name)}${songs ? `（${songs} 首歌在用）` : ''}`;
      }
      // 導演在調整舞台把機關位置存回 Cue 庫 / 復原（用到這個 cue 的歌一起變）
      if (p.kind === 'stage_save' || p.kind === 'stage_undo') {
        const changed = num(p.changed);
        const songs = Array.isArray(p.songs) ? p.songs.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [];
        const total = num(p.songCount);
        const list = total != null && total > songs.length ? `${songs.join('、')} 等 ${total} 首` : songs.join('、');
        const together = songs.length > 0 ? `${changed != null ? '，' : ''}${list} 會一起變` : '';
        const inner = `${changed != null ? `改了 ${changed} 個機關` : ''}${together}`;
        return p.kind === 'stage_undo'
          ? `復原 Cue 庫的${full(lab, name)}的舞台調整${inner ? `（${inner}）` : ''}`
          : `在調整舞台存回 Cue 庫的${full(lab, name)}${inner ? `（${inner}）` : ''}`;
      }
    }
  }
  // 這首歌的 cue 指到 Cue 庫的 cue（加、換、改時間、拿掉）
  if (type === 'cue' && str(p.libraryLabel) !== undefined) {
    const song = str(p.songName);
    const lib = quoted([str(p.libraryLabel), str(p.libraryName)].filter(Boolean).join(' '));
    const at = num(p.videoTimeSec);
    const t = at != null ? fmtClock(at) : '';
    const songPart = song ? `歌曲${quoted(song)}` : '這首歌';
    if (action === 'create') return `在${songPart}${t} 加上${lib}`;
    if (action === 'delete') return `從${songPart}拿掉${t ? ` ${t} 的` : ''}${lib}`;
    if (action === 'update' && p.kind === 'repoint') return `把${songPart}${t} 的 cue 換成${lib}`;
    if (action === 'update' && p.kind === 'crossfade') {
      const to = num(p.toSeconds);
      const cue = cueName(str(p.libraryLabel), str(p.libraryName));
      return to == null || p.useLibrary === true
        ? `${songPart}的 ${cue}轉場改回用 Cue 庫的`
        : `${songPart}的 ${cue}另外設轉場 ${fmtSec(to)} 秒`;
    }
    if (action === 'update' && p.kind === 'retime') {
      const from = num(p.fromTimeSec), to = num(p.toTimeSec);
      return `把${songPart}${from != null ? fmtClock(from) : ''} 的 cue 時間${to != null ? `改成 ${fmtClock(to)}` : '清掉'}`;
    }
  }

  // 歌曲狀態改了（含交新版後自動回到審查中）；同時還改了別的欄位 → 往下走，一起寫
  if (type === 'song' && action === 'update' && str(p.internalStatusTo) && !str(p.statusTo)) {
    const to = str(p.internalStatusTo) ?? '';
    const from = str(p.internalStatusFrom);
    const lbl = (x: string) => SONG_STATUS_LABEL[x] ?? x;
    const base = from && from !== to ? `內部狀態：歌曲${quoted(name)}從「${lbl(from)}」改成「${lbl(to)}」` : `內部狀態：歌曲${quoted(name)}改成「${lbl(to)}」`;
    return p.auto ? `${base}（交了新版${str(p.label) ? ` ${str(p.label)}` : ''}，自動改的）` : base;
  }
  if (type === 'song' && action === 'update') {
    const others = changeFragments('song', p, ['status']);
    const from = str(p.statusFrom);
    const to = str(p.statusTo);
    const behalf = p.onBehalf === true ? '（製作代導演）' : '';
    if (others.length === 0) {
      if (from && to && from !== to) {
        const base = `把歌曲${quoted(name)}從「${SONG_STATUS_LABEL[from] ?? from}」改成「${SONG_STATUS_LABEL[to] ?? to}」${behalf}`;
        return p.auto ? `${base}（交了新版${str(p.label) ? ` ${str(p.label)}` : ''}，自動改的）` : base;
      }
      // 同一個狀態再決定一次（已通過 V1 → 通過 V2）
      if (from && to && from === to && (to === 'approved' || to === 'needs_changes')) {
        return `把歌曲${quoted(name)}再標一次「${SONG_STATUS_LABEL[to]}」${behalf}`;
      }
    }
  }
  // 刪歌 = 進「最近刪除的歌」（0050：payload.trashed）；救回來；超過 30 天排程清掉（系統做的）。以前真的刪掉的舊紀錄沒有 trashed → 照一般的「刪除歌曲」
  if (type === 'song') {
    if (action === 'delete' && p.trashed === true) return `刪除歌曲${quoted(name)}（30 天內可以到「最近刪除的歌」救回來）`;
    if (action === 'restore') return `從「最近刪除的歌」救回歌曲${quoted(name)}`;
    if (action === 'purge') return `歌曲${quoted(name)}刪除超過 30 天，系統把它清掉了`;
  }
  // 影片版本
  if (type === 'song_video') {
    const label = str(p.label);
    const title = str(p.title);
    if (action === 'create') return `交了影片${label ? ` ${label}` : ''}${quoted(title)}`;
    if (action === 'delete') return `刪除影片${label ? ` ${label}` : ''}${quoted(title)}`;
    if (action === 'restore') return `從最近刪除救回影片${label ? ` ${label}` : ''}${quoted(title)}`;
  }
  // 歌曲的音樂（cf-worker/songMusic.mjs）：上傳 / 換 / 移除（payload 有歌名和檔名）
  if (type === 'song_music') {
    const song = str(p.songName);
    const file = str(p.filename);
    const of = song ? `「${song}」的` : '這首歌的';
    if (action === 'upload') return `上傳了${of}音樂${quoted(file)}`;
    if (action === 'update') return `換了${of}音樂${file ? `，改成${quoted(file)}` : ''}`;
    if (action === 'delete') return `移除了${of}音樂${quoted(file)}`;
  }
  // 專案：結束清影片（storage.mjs）、製作尺寸、還原封存、Cue 庫搬家、複製 / 匯入
  if (type === 'project' && p.wrapUp === true) {
    const removed = num(p.removedCount) ?? 0;
    const moved = num(p.queuedForInfrequent) ?? 0;
    const bits = [removed ? `清掉 ${removed} 版影片（30 天內可以救回來）` : '', moved ? `${moved} 版改成長期存放` : ''].filter(Boolean);
    return `專案結束清影片${bits.length ? `：${bits.join('、')}` : ''}`;
  }
  if (type === 'project' && action === 'update') {
    // 後台存分數字串（'1/2'）；畫面上跟製作尺寸視窗一樣寫成 50% / 原尺寸
    const scale = (s: string | undefined) => `「${workScaleText(parseWorkScale(s))}」`;
    if (str(p.workScale)) return `把製作尺寸從${scale(str(p.was))}改成${scale(str(p.workScale))}`;
    if (p.restored === true) return '還原封存的專案';
    if (p.cueLibraryMigration && name) return name;
  }
  if (type === 'project' && action === 'create') {
    if (str(p.duplicatedFromName)) return `從專案${quoted(str(p.duplicatedFromName))}複製出專案${quoted(name)}`;
    if (str(p.importedFrom) && str(p.importedFrom) !== 'unknown') return `匯入專案${quoted(name)}（原本叫${quoted(str(p.importedFrom))}）`;
    if (p.importedFrom) return `匯入專案${quoted(name)}`;
  }
  // 專案成員：加進專案 / 改角色 / 移出專案（後台寫好一句 summary）
  if (type === 'member') {
    const summary = str(p.summary);
    if (summary) return summary;
    if (action === 'delete') return name ? `把${quoted(name)}移出專案` : '移出了一位成員';
    return name ? `調整成員${quoted(name)}的角色` : '調整了一位成員的角色';
  }
  // 邀請連結：建立（寫給誰、什麼角色）/ 撤銷（同樣寫是誰的、什麼角色；以前的紀錄沒有就寫「一條」）
  if (type === 'invite') {
    const role = MEMBER_ROLE_NAME[String(p.role) as MemberRole];
    if (action === 'create') return `建立邀請連結${quoted(name)}${role ? `（${role}）` : ''}`;
    if (action === 'delete') return `撤銷${name ? `${quoted(name)}的` : '一條'}邀請連結${role ? `（${role}）` : ''}`;
  }
  // 模型：上傳（檔名、大小）/ 切換版本 / 刪除版本
  if (type === 'model') {
    const file = str(p.fileName);
    const size = num(p.sizeBytes);
    const sizeText = size ? fmtSize(size) : undefined;
    const when = modelKeyTime(str(p.toKey) ?? str(p.key));
    const which = file ? quoted(file) : '';
    if (action === 'upload') {
      if (p.autoUv === true) {
        const lc = isObj(p.ledCanvas) ? p.ledCanvas : null;
        const w = num(lc?.width), h = num(lc?.height);
        return `套用自動貼 UV 的新模型${paren([w && h ? `LED 畫布 ${w}×${h}` : undefined])}`;
      }
      return `上傳模型${which}${paren([sizeText, p.held === true && '先放著，還沒換上去'])}`;
    }
    if (action === 'activate') {
      const assetName = str(p.assetName);
      if (assetName) return `改用模型庫的「${assetName}」`;
      return `切換模型版本${which}${paren([!file && when ? `${when} 上傳的` : undefined, sizeText])}`;
    }
    if (action === 'delete') {
      return `刪除模型版本${which}${paren([!file && when ? `${when} 上傳的` : undefined, sizeText])}`;
    }
  }
  // Drive 檔案手動歸到某首歌
  if (type === 'drive_file' && action === 'update') {
    const file = str(p.fileName), song = str(p.songName);
    const f = file ? `檔案${quoted(file)}` : '檔案';
    if (song) return `把 Drive ${f}歸到歌曲${quoted(song)}`;
    if ('songId' in p && p.songId === null) return `把 Drive ${f}改成未歸類`;
  }
  // AE 打包檔（製作替每個版型上傳做好的壓縮檔）：上傳 / 換新 / 刪除
  if (type === 'ae_package') {
    const tpl = str(p.templateName);
    const file = str(p.filename);
    const size = num(p.sizeBytes);
    const what = tpl ? `版型${quoted(tpl)}的 AE 打包檔` : ' AE 打包檔';
    const sizeText = paren([size ? fmtSize(size) : undefined]);
    if (action === 'upload') return `上傳${what}${quoted(file)}${sizeText}`;
    if (action === 'update') return `換掉${what}，改成${quoted(file) || '新的'}${sizeText}`;
    if (action === 'delete') return `刪除${what}${quoted(file)}`;
  }
  // 審查流程（只有製作和視覺統籌看得到）：放行 / 退回 / 轉交
  if (type === 'song_gate') {
    const song = quoted(str(p.songName));
    const v = num(p.version);
    const ver = v != null ? ` V${v}` : '';
    if (p.step === 'release') return `放行歌曲${song}${ver}，給導演看`;
    if (p.step === 'return') return `把歌曲${song}${ver}退回給動畫師繼續改`;
    if (p.step === 'relay') return p.approved === true ? `轉告動畫師歌曲${song}通過了` : `把導演的意見轉交給動畫師（歌曲${song}）`;
  }
  // 版型：新增 / 修改 / 刪除；歌的版型時間線
  if (type === 'led_template' && action === 'create') {
    const screens = num(p.screens);
    return `新增版型${quoted(name)}${screens != null ? `（${screens} 塊螢幕）` : ''}`;
  }
  if (type === 'song_template_timeline') {
    const song = str(p.songName);
    const start = str(p.startName) ?? (p.start ? undefined : '全部');
    const switches = num(p.switches);
    return `設定歌曲${quoted(song)}的版型時間線${paren([start ? `開頭用${quoted(start)}` : undefined, switches ? `中途換 ${switches} 次` : '中途不換'])}`;
  }
  if (action === 'reorder') {
    const count = num(p.count);
    return count != null ? `重新排序 ${count} ${join("個", target)}` : join("重新排序", target);
  }
  // 對開拆成兩個各自動的機關
  if (type === 'mechanism' && action === 'update' && p.unsplit === true) {
    const a = str(p.nameA), b = str(p.nameB);
    return `把對開機關${quoted(name)}拆成兩個機關${a && b ? `${quoted(a)}${quoted(b)}` : ''}`;
  }
  // 舊的 cue 位置轉成機關位置
  if (type === 'mechanism' && action === 'update' && num(p.convertedCues) != null) {
    const skipped = num(p.skippedCues) ?? 0;
    return `把 ${num(p.convertedCues)} 個 cue 的物件位置轉成機關${quoted(name)}的位置${skipped ? `（略過 ${skipped} 個）` : ''}`;
  }
  // 機關新增 / 刪除（吊掛、對開的說得清楚一點）
  if (type === 'mechanism' && action === 'create') {
    const hoists = num(p.hoists), objects = num(p.objects);
    if (p.variant === 'hoist_rig') return `新增吊掛${quoted(name)}${paren([hoists != null ? `${hoists} 個吊點` : undefined])}`;
    return `新增${p.variant === 'split' ? '對開' : ''}機關${quoted(name)}${paren([objects ? `${objects} 個物件` : undefined])}`;
  }
  if (type === 'mechanism' && action === 'delete') {
    const removed = num(p.removedPositions);
    return `刪除${p.variant === 'hoist_rig' ? '吊掛' : '機關'}${quoted(name)}${paren([removed ? `連同 ${removed} 筆 cue 位置` : undefined])}`;
  }
  // 一排機關（機關 v2 第 3 階段）
  if (type === 'mechanism' && action === 'bulk_create') {
    const count = num(p.count);
    return `一次建立${count != null ? ` ${count} 個` : ''}機關${quoted(name)}`;
  }
  if (type === 'mechanism_group') {
    if (action === 'delete') return `刪除一排機關${quoted(name)}${num(p.mechanisms) != null ? `（連同 ${num(p.mechanisms)} 個機關）` : ''}`;
    if (action === 'update' && p.dissolved === true) return `解散一排機關${quoted(name)}（${num(p.mechanisms) ?? 0} 個機關留著）`;
  }
  if (action === 'bulk_create') {
    if (type === 'cue') {
      const cues = num(p.cuesInserted) ?? num(p.inserted);
      const states = num(p.statesInserted);
      if (cues != null && p.importedFrom) return `從別首歌匯入 ${cues} 個 cue${paren([states ? `${states} 筆物件位置` : undefined])}`;
      return `批次建立${cues != null ? ` ${cues} 個` : ''} cue`;
    }
    const inserted = num(p.inserted), skipped = num(p.skipped) ?? 0, deleted = num(p.deleted) ?? 0;
    return `從模型建立${inserted != null ? ` ${inserted} 個` : ''}物件${paren([deleted ? `先刪掉原本的 ${deleted} 個` : undefined, skipped ? `${skipped} 個已經有了，略過` : undefined])}`;
  }
  if (action === 'seed') {
    const inserted = num(p.inserted);
    return `加入${inserted != null ? ` ${inserted} 個` : ''}範例物件`;
  }
  // 物件「預設隱藏」開關（只改了這一項）
  if (type === 'stage_object' && action === 'update') {
    const list = changesOf('stage_object', p);
    const only = list && list.length === 1 && list[0].field === 'default_hidden' && list[0].hasTo && typeof list[0].to === 'boolean' ? list[0] : null;
    if (only) {
      const on = only.to === true;
      return `${on ? '把' : '取消'}物件${quoted(name)}${on ? '設成預設隱藏' : '的預設隱藏'}`;
    }
  }
  // 新增物件（寫是哪一類）
  if (type === 'stage_object' && action === 'create') {
    const cat = str(p.category);
    return `新增物件${quoted(name)}${paren([cat && CATEGORY_LABEL[cat]])}`;
  }
  // 一首歌所有 cue 的物件一次回到預設位置 / 復原
  if (type === 'song_cue_states') {
    const songName = str(p.songName);
    if (action === 'reset') {
      const removed = num(p.removed);
      return `把歌曲${quoted(songName)}所有 cue 的物件重置回預設位置${removed != null ? `（清掉 ${removed} 筆）` : ''}`;
    }
    const restored = num(p.restored);
    return `復原歌曲${quoted(songName)}所有 cue 的物件位置${restored != null ? `（寫回 ${restored} 筆）` : ''}`;
  }
  if (type === 'cue_state' && action === 'reset') {
    const cueName = str(p.cueName);
    const objectName = str(p.objectName);
    return `把${cueName ? `「${cueName}」` : ''} cue 裡的${quoted(objectName) || '物件'}重置回預設位置`;
  }
  // 重置整個 cue 的物件位置
  if (type === 'cue' && action === 'reset') {
    const removed = num(p.removed);
    return `把 ${name ? `cue${quoted(name)}` : 'cue '}的物件位置重置回預設${removed ? `（清掉 ${removed} 筆）` : ''}`;
  }

  // 修改：寫出改了什麼（歌、專案、cue、物件、機關、版型…）
  if (action === 'update' && FIELDS[type]) {
    const frags = changeFragments(type, p);
    if (frags.length) {
      const head = type === 'mechanism_group' ? `修改一排機關${quoted(name)}` : `${join(verb, target)}${quoted(name)}`;
      return withDetails(head, frags);
    }
  }
  if (type === 'mechanism_group' && action === 'update') {
    return `修改一排機關${quoted(name)}${p.shared === true ? '的共用設定' : ''}`;
  }
  return `${join(verb, target)}${quoted(name)}`;
}
